// Left-sidebar navigation. Nav consolidation (2026-06-27): the ~25 flat items
// Jesse asked us to pare down now live inside a handful of HUBS — one sidebar
// entry per hub, each holding its related pages as internal tabs (see HubPage).
// Office staff navigate the hubs; field roles get a direct entry for their own
// tool (they never see the office hubs). Reuses .sidebar / .nav-section /
// .nav-item CSS in main.css.
import { icon } from './icons';
// `only:true` makes `roles` an EXCLUSIVE allow-list — even admin is excluded.
// Used for the field-role direct entries so office/admin see the hub instead.
interface SideItem { id: string; icon: string; label: string; roles?: string[]; only?: boolean }
interface SideBucket { title: string; items: SideItem[] }

// Role-based nav (PDF Notes-10 #13). Back-office roles see the office hubs; a
// field role sees only its own tool (an `only` entry); admin sees every hub.
const OFFICE = ['admin', 'director', 'manager', 'remote_ops'];

const BUCKETS: SideBucket[] = [
  // Field/owner roles land here on their own tool only (exclusive — hidden from
  // office/admin, who reach the same pages through the hubs below).
  { title: 'My Work', items: [
    { id: 'owner-portal', icon: 'home', label: 'Owner Portal', roles: ['owner'], only: true },
    { id: 'tasks', icon: 'target', label: 'My Tasks', roles: ['cleaner', 'inspector', 'maintenance'], only: true },
    { id: 'housekeeping', icon: 'sparkles', label: 'Housekeeping', roles: ['cleaner'], only: true },
    { id: 'inspections', icon: 'search', label: 'Inspections', roles: ['inspector'], only: true },
    { id: 'maintenance', icon: 'wrench', label: 'Maintenance', roles: ['maintenance'], only: true },
    { id: 'supplies', icon: 'package', label: 'Supplies', roles: ['maintenance'], only: true },
    { id: 'ffe', icon: 'home', label: 'FF&E', roles: ['inspector', 'maintenance'], only: true },
    { id: 'issues', icon: 'flag', label: 'Issues', roles: ['inspector', 'maintenance'], only: true },
    { id: 'field-log', icon: 'flag', label: 'Log Issues', roles: ['cleaner', 'inspector', 'maintenance'], only: true },
    { id: 'eod', icon: 'book', label: 'Daily Ops', roles: ['cleaner', 'inspector', 'maintenance'], only: true },
  ] },
  // The office hubs — each one sidebar entry, related pages as internal tabs.
  { title: 'Workspace', items: [
    { id: 'stays', icon: 'calendar', label: 'Calendar' },   // R110 #1: the stays timeline (office roles by default)
    { id: 'field-log', icon: 'flag', label: 'Log Issues', roles: ['admin', 'director', 'manager', 'remote_ops'] },
    { id: 'eod', icon: 'book', label: 'Daily Ops', roles: ['admin', 'director', 'manager', 'remote_ops'] },
    // R111 (Jesse: "move the tasks board by itself, with its own tab. Issues
    // should be by themselves as well. Move Guest Recovery to its own tab"):
    // the Tasks & Issues hub is dissolved — three first-class office rows.
    // Explicit roles (not the OFFICE default) because 'tasks' / 'issues' also
    // exist above as field `only` rows — the field-log / eod duplicate-id pattern.
    { id: 'tasks', icon: 'target', label: 'Tasks', roles: [...OFFICE] },
    { id: 'issues', icon: 'flag', label: 'Issues', roles: [...OFFICE] },
    { id: 'guest-recovery', icon: 'heart', label: 'Guest Recovery', roles: [...OFFICE] },
    // R105 #6 (Jesse): Mid-Term Rentals is its own entry; Arrivals moved into Remote Operations.
    { id: 'mtr', icon: 'home', label: 'Mid-Term Rentals' },
    // R54: promoted out of Remote Ops — texting guests is an all-day surface.
    { id: 'guest-texts', icon: 'chat', label: 'Messaging', roles: ['admin', 'director', 'manager', 'remote_ops'] },   // R112: Guest Texts → Messaging (the universal SMS + PMS inbox); id kept for every handoff
    { id: 'data-explorer', icon: 'trending', label: 'AI Revenue Manager' },
    { id: 'analytics', icon: 'compass', label: 'Analytics' },
    { id: 'listing-quality', icon: 'star', label: 'Listing Quality' },
    { id: 'pnl', icon: 'wallet', label: 'P&L Review', roles: ['admin', 'director'] },
    { id: 'portfolio', icon: 'building', label: 'Portfolio' },
    { id: 'hk-insp', icon: 'sparkles', label: 'Housekeeping & Inspections' },
    { id: 'maintenance', icon: 'wrench', label: 'Maintenance' },
    // R100 A2 (Jesse): one Accounting hub — Supplies & Expenses, Resolutions,
    // Special Offers, Internal Expenses, Statement Reviewer. The external
    // accounting partner role sees ONLY this entry.
    // R105 #6: remote_ops added — Resolutions moved here from Remote Ops (R48 kept them on the ledger; the server allows it)
    { id: 'accounting', icon: 'receipt', label: 'Accounting', roles: ['admin', 'director', 'manager', 'remote_ops', 'accounting'] },
    { id: 'onboarding', icon: 'package', label: 'Onboarding' },
    { id: 'sops', icon: 'book', label: 'SOPs' },
    // R111: Damages & Claims moved in here (from the dissolved Tasks & Issues
    // hub); icon 'clock' — 'calendar' became the Calendar entry's glyph in R110.
    { id: 'remote-ops', icon: 'clock', label: 'Remote Operations' },
    { id: 'owner-ops', icon: 'users', label: 'Owner Operations' },
    { id: 'jarvis', icon: 'sparkles', label: 'Jarvis' },
  ] },
  { title: 'Settings', items: [
    { id: 'users', icon: 'users', label: 'Users', roles: ['admin', 'director', 'manager'] },
    { id: 'admin', icon: 'gear', label: 'Admin', roles: [...OFFICE] },
    { id: 'glossary', icon: 'book', label: 'Glossary' },
  ] },
];

// A page reached via deep-link / search keeps its HUB highlighted. (Field-tool
// ids — housekeeping/inspections/supplies/ffe/issues — are left out on
// purpose: field roles need those ids to match their own direct entry.
// maintenance/onboarding/sops are first-class Workspace entries, so they
// highlight on their own id. R111: tasks / issues / guest-recovery are
// first-class too (office rows above) and highlight on their own id.)
const ALIAS: Record<string, string> = {
  'today': 'analytics', 'impact': 'analytics', 'reports': 'analytics',
  'gap-nights': 'analytics', 'event-pricing': 'analytics', 'vacancies': 'analytics',
  'money': 'analytics', 'projections': 'analytics', 'booking-quote': 'analytics',
  'triage': 'listing-quality', 'reviews': 'listing-quality',
  'statements': 'accounting', 'supexp': 'accounting', 'resolutions': 'accounting', 'internal-expenses': 'accounting', 'special-offers': 'accounting',
  'profitability': 'pnl',
  'listings': 'portfolio', 'reservations': 'portfolio',
  'arrivals': 'remote-ops', 'team': 'remote-ops',   // R105 #6: Arrivals is a Remote Operations tab; MTR is first-class
  'claims': 'remote-ops',   // R111: Damages & Claims is a Remote Operations tab (the 'claims' id redirects there)
  'owners': 'owner-ops', 'owner-profile': 'owner-ops',
  'field-ops': 'hk-insp',
  'operations': 'admin', 'settings': 'admin',
};

export class Sidebar {
  private currentActive: string = 'analytics';
  private onNavigate: (page: string) => void;

  constructor(onNavigate: (page: string) => void) { this.onNavigate = onNavigate; }

  setActive(page: string): void { this.currentActive = ALIAS[page] || page; }

  render(): string {
    // Role from the signed-in profile (set by main.ts on login). Default to
    // 'admin' when absent (local dev / no session) so nothing is hidden.
    let role = 'admin';
    try { role = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('userRole')) || 'admin'; } catch (_) { /* */ }
    // `only` entries are exclusive to their listed roles (admin included out);
    // everything else follows the office default unless it carries its own list.
    const canSee = (it: SideItem) =>
      it.only ? (it.roles ? it.roles.includes(role) : false)
              : role === 'admin' || (it.roles ? it.roles.includes(role) : OFFICE.includes(role));
    const item = (it: SideItem) => `
      <div class="nav-item ${this.currentActive === it.id ? 'active' : ''}" data-page="${it.id}" title="${it.label}">
        <span class="icon">${icon(it.icon)}</span><span class="nav-label">${it.label}</span>
      </div>`;
    const section = (b: SideBucket) => {
      const items = b.items.filter(canSee);
      if (!items.length) return '';
      return `
      <nav class="nav-section">
        <div class="nav-section-title">${b.title}</div>
        ${items.map(item).join('')}
      </nav>`;
    };
    // R110 #1 (HostBuddy): brand mark, nav sections in a scroll area, and a
    // pinned footer with "Collapse menu" + "Log out". The collapsed rail is
    // remembered per device (localStorage) and re-applied on every render —
    // App rebuilds this HTML on each navigation.
    const collapsed = Sidebar.isCollapsed();
    return `
      <aside class="sidebar${collapsed ? ' collapsed' : ''}" id="app-sidebar">
        <div class="sidebar-logo">
          <h1 title="WCHDash"><span class="brand-mark">${icon('logo', 20)}</span><span>WCHDash</span></h1>
          <button class="sidebar-close-btn" id="sidebar-close-btn" aria-label="Close menu">✕</button>
        </div>
        <div class="sidebar-scroll">${BUCKETS.map(section).join('')}</div>
        <div class="sidebar-foot">
          <div class="nav-item sb-collapse" id="sb-collapse" title="${collapsed ? 'Expand menu' : 'Collapse menu'}"><span class="icon">${icon('collapse')}</span><span class="nav-label">Collapse menu</span></div>
          <div class="nav-item" id="sb-signout" title="Log out"><span class="icon">${icon('logout')}</span><span class="nav-label">Log out</span></div>
        </div>
      </aside>`;
  }
  static isCollapsed(): boolean { try { return localStorage.getItem('sidebar_collapsed') === '1'; } catch { return false; } }
  static applyCollapsed(on: boolean): void {
    try { localStorage.setItem('sidebar_collapsed', on ? '1' : '0'); } catch { /* private mode */ }
    document.documentElement.toggleAttribute('data-sidebar', false);
    if (on) document.documentElement.setAttribute('data-sidebar', 'collapsed');
    document.getElementById('app-sidebar')?.classList.toggle('collapsed', on);
    const btn = document.getElementById('sb-collapse'); if (btn) btn.title = on ? 'Expand menu' : 'Collapse menu';
  }

  bindEvents(): void {
    document.querySelectorAll<HTMLElement>('#app-sidebar .nav-item[data-page], #app-sidebar .sidebar-logo').forEach(el => {
      el.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const page = target.dataset.page;
        document.body.classList.remove('sidebar-mobile-open');
        if (page) this.onNavigate(page);
      });
    });
    document.getElementById('sidebar-close-btn')?.addEventListener('click', (e) => {
      e.stopPropagation();
      document.body.classList.remove('sidebar-mobile-open');
    });
    // the rail width lives on <html> so .app-main's margin follows it
    Sidebar.applyCollapsed(Sidebar.isCollapsed());
    document.getElementById('sb-collapse')?.addEventListener('click', (e) => { e.stopPropagation(); Sidebar.applyCollapsed(!Sidebar.isCollapsed()); });
    document.getElementById('sb-signout')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      try { const { supabase } = await import('../../lib/supabase'); await supabase.auth.signOut(); } catch (_) { /* fall through */ }
      window.location.href = '/login';
    });
  }
}
